/**
 * useHouseholdDietary — én spørring mot `GET /api/members/dietary` for både
 * allergibrikken og allergiarket på handlelista (#148).
 *
 * Før hadde brikken og arket hvert sitt endepunkt og hver sin cache (brikken
 * en time, arket fem minutter), og serverens memo ble aldri tømt. Da kunne
 * brikken vise allergier fra før en admins endring og være uenig med arket.
 * Nå leser begge de samme radene, og de hentes på nytt hver gang handlelista
 * eller arket monteres (`staleTime: 0`).
 */
import { useQuery } from '@tanstack/react-query';
import { ALLERGY_CHIPS, DIETARY_CHIPS, type MemberDietaryRow } from '@stell/shared';
import { sendJson } from '../../lib/api';

export const HOUSEHOLD_DIETARY_KEY = ['members', 'dietary'] as const;

export function useHouseholdDietary() {
  return useQuery({
    queryKey: HOUSEHOLD_DIETARY_KEY,
    queryFn: async () =>
      (await sendJson<{ members: MemberDietaryRow[] }>('/api/members/dietary')).members,
    staleTime: 0,
  });
}

/**
 * Kjente brikker i leksikonrekkefølge, deretter ukjente som de er. En lagret
 * id utenfor dagens leksikon (eldre eller omdøpt) skal vises, ikke forsvinne.
 */
export function chipLabels(
  chips: readonly string[],
  lexicon: readonly string[],
  label: (k: string) => string,
): string[] {
  return [
    ...lexicon.filter((k) => chips.includes(k)).map(label),
    ...chips.filter((k) => !lexicon.includes(k)),
  ];
}

type ChipList = { chips: readonly string[]; custom: readonly string[] };

/**
 * Et medlems allergier og kosthold som visningsnavn: kjente brikker, så egne
 * oppføringer. Delt av allergiarket her og DietaryPills på Familie (#159).
 */
export function dietaryLabels(
  d: { allergies: ChipList; dietary: ChipList },
  t: (key: string) => string,
): { allergies: string[]; dietary: string[] } {
  return {
    allergies: [
      ...chipLabels(d.allergies.chips, ALLERGY_CHIPS, (k) => t(`allergy.chip.${k}`)),
      ...d.allergies.custom,
    ],
    dietary: [
      ...chipLabels(d.dietary.chips, DIETARY_CHIPS, (k) => t(`dietary.chip.${k}`)),
      ...d.dietary.custom,
    ],
  };
}
